/**
 * Selected work. Each row mirrors the reference layout (measured at 1280px wide):
 * - "large": ~49% wide, roughly square (617×550)
 * - "small": ~33% wide, dropped ~60px lower (407×343)
 * - "wide":  ~66% wide, indented ~17% from the left (16:10)
 */
export type ProjectSize = 'large' | 'small' | 'wide';

export interface Project {
  /** Brand name, one or two words */
  client: string;
  /** One line on what was done */
  summary: string;
  /** Service areas, shown as a dot-separated line */
  services: string[];
  image: string;
  alt: string;
  size: ProjectSize;
  /** Wide rows only: which edge the visual hugs (default right) */
  align?: 'left' | 'right';
  /** Live project — opens in a new tab */
  href?: string;
}

export const projectRows: Project[][] = [
  [
    {
      client: 'Project Cars',
      summary: 'A real-time 3D car configurator in a futuristic showroom',
      services: ['3D Configurator', 'WebGL', 'Webflow'],
      image: 'https://cdn.magicpatterns.com/uploads/wZapfH4iYV29gbXFiMVr5Y/6ade21fce2dc8606047e6cd7_Project_Cars.png',
      alt: 'White Mercedes-AMG GT on a turntable in a bright futuristic showroom with snowy mountains behind',
      size: 'large',
      href: 'https://projectcars.webflow.io/',
    },
    {
      client: 'Haunted House',
      summary: 'An eerie floating island you explore, chapter by chapter',
      services: ['3D Storytelling', 'WebGL', 'AI'],
      image: 'https://cdn.magicpatterns.com/uploads/h7QtiHsmDSUsqeGfjyuuEh/6ade21fce2dc8606047e6c5d_HFH_18x9_ca-compress.png',
      alt: 'Glowing haunted mansion with jack-o-lanterns on a floating island in a misty night sky',
      size: 'small',
      href: 'https://hauntedhouse.webflow.io/',
    },
  ],
  [
    {
      client: 'Starmax',
      summary: 'A spatial portfolio where scenes replace pages (Coming soon)',
      services: ['Spatial Website', 'Real-time 3D', 'WebGL'],
      image: 'https://cdn.magicpatterns.com/uploads/wZrnXfnsaxP4ZhVnKwHEV9/6ade21fce2dc8606047e6d1c_image_10.avif',
      alt: 'Starmax logo with a shooting star glowing over a deep blue nebula',
      size: 'wide',
    },
  ],
  [
    {
      client: 'Unique Jewelry',
      summary: 'An ultra-realistic 3D ring and diamond configurator',
      services: ['3D Configurator', 'E-Commerce', 'UI/UX'],
      image: 'https://cdn.magicpatterns.com/uploads/3CVZ6Yj7vkKqrobiYJYUro/6ade21fce2dc8606047e6c1e_640f09de626d6cf9f1342a53_Mask_Group_79.webp',
      alt: 'Close-up of a diamond ring in white and yellow gold beside the headline Unique Jewelry on pink',
      size: 'large',
      href: 'https://webgi-jewelry.vercel.app/',
    },
    {
      client: 'Ola Research Lab',
      summary: 'An interactive 3D home for an architecture research lab',
      services: ['Web Design', '3D', 'Development'],
      image: 'https://cdn.magicpatterns.com/patterns/generated-images/9f0b4212-0d84-4022-b46a-eddad04c4d09.jpg',
      alt: 'Curved white research lab building with glass bands floating in a pale grey studio',
      size: 'small',
      href: 'https://ola-research.vercel.app/',
    },
  ],
  [
    {
      client: 'Windland',
      summary: 'A playful low-poly world that tells the story of wind energy',
      services: ['3D World', 'WebGL', 'Storytelling'],
      image: 'https://cdn.magicpatterns.com/uploads/kumHYddSKdaTvmpmmXzMnb/6ade21fce2dc8606047e6c7b_640f07d83a879fa4d662d605_Mask_Group_86.avif',
      alt: 'Low-poly 3D landscape with a wind turbine, pine forest and a small city of colourful towers',
      size: 'small',
      href: 'https://windland-neotix.vercel.app/',
    },
    {
      client: 'Implantação',
      summary: 'An interactive 3D city map for exploring real estate sites',
      services: ['3D Map', 'Real Estate', 'UI/UX'],
      image: 'https://cdn.magicpatterns.com/uploads/tHTgDTcq4kuV5PP6eGPLPN/6ade21fce2dc8606047e6c7c_640ef2f19a6d5b494f108ebd_Mask_Group_94.avif',
      alt: 'White 3D model of a city along a winding blue river with purple location markers',
      size: 'large',
      href: 'https://fgr-implantacao.vercel.app/',
    },
  ],
];
